import React, { useEffect, useState, useRef, useCallback } from 'react';
import useZoom from '../../hooks/useZoom';
import { resolveRefToElement } from '../../utils/refResolver';
import styles from './styles.module.scss';

export default function UIHelperPopup({ helper }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef();
  const { getCorrectedRect, onZoomChange } = useZoom();

  const calculatePosition = useCallback(() => {
    const element = resolveRefToElement(helper.ref);
    if (!element) return;

    const targetRect = getCorrectedRect(element);
    if (!targetRect) return;

    let top = 0;
    let left = 0;

    // Position based on helper.position with zoom-corrected coordinates
    switch (helper.position) {
      case 'top':
        top = targetRect.top - helper.offset - 8; // extra 8px to fit arrow height
        left = targetRect.left + targetRect.width / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + helper.offset;
        left = targetRect.left + targetRect.width / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left - helper.offset; // Start at target's left edge
        // We'll use CSS transform to shift it left by its width
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.right + helper.offset;
        break;
      default:
        top = targetRect.bottom + helper.offset;
        left = targetRect.left + targetRect.width / 2;
    }

    setPosition({ top, left });
  }, [helper, getCorrectedRect]);

  useEffect(() => {
    calculatePosition();

    const handleResize = () => calculatePosition();
    window.addEventListener('resize', handleResize);

    const cleanupZoom = onZoomChange(() => {
      setTimeout(calculatePosition, 0);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupZoom();
    };
  }, [calculatePosition, onZoomChange]);

  const transforms = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
  };
  const transformStyle = transforms[helper.position] || transforms.bottom;

  const arrowPositionClasses = {
    top: styles.uiHelperArrowTop,
    bottom: styles.uiHelperArrowBottom,
    left: styles.uiHelperArrowLeft,
    right: styles.uiHelperArrowRight,
  };
  const arrowClass = [
    styles.uiHelperArrow,
    arrowPositionClasses[helper.position] || styles.uiHelperArrowBottom,
  ].join(' ');

  return (
    <div
      ref={popupRef}
      className={styles.uiHelperPopup}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 100003,
        transform: transformStyle,
      }}
    >
      {helper.arrow && <div className={arrowClass} />}
      <div
        className={styles.uiHelperContent}
        style={{ fontSize: `${helper.fontSize}px` }}
      >
        {helper.content}
      </div>
    </div>
  );
}
